/** @file This file contains utilities to work with promises. */
import moment from 'moment';
import { always, EitherAsync, identity } from 'purify-ts';
import { coerceToError } from './errors';
/**
 * Convert a function that produces a promise to an `EitherAsync`.
 *
 * @param func The function to produce a promise.
 * @returns An EitherAsync, which will resolve to a `Right` if the promises resolves, and resolves
 * to a `Left` if it rejects. The error value will always be a (sub)type of `Error`.
 */
export function toEitherAsync(func) {
    return EitherAsync(async ({ throwE }) => {
        try {
            return await func();
        }
        catch (error) {
            return throwE(coerceToError(error));
        }
    });
}
/**
 * Convert a promise to an `EitherAsync` that has already been started.
 *
 * @param promise The promise to convert to an either async.
 * @returns An EitherAsync that has already started running.
 */
export function toEitherAsyncEager(promise) {
    return toEitherAsync(() => promise);
}
/**
 * Convert a function that returns a promise to return an EitherAsync.
 *
 * @param func The function to convert.
 * @returns A function that returns an `EitherAsync`, as described in `toEitherAsync`.
 */
export function eitherAsyncify(func) {
    return (...args) => toEitherAsync(() => func(...args));
}
/** A wrapper around `Promise.resolve` that has `Promise` bound to it. */
export const resolve = Promise.resolve.bind(Promise);
/**
 * Sleep for the given amount of time.
 *
 * @param time The amount of time to sleep.
 * @returns A promise that will resolve after `time`.
 */
export function sleep(time) {
    return new Promise(_resolve => {
        setTimeout(() => {
            _resolve(undefined);
        }, time.asMilliseconds());
    });
}
/**
 * Retry a function that returns a promise until the returned promise no longer rejects.
 *
 * @param doTry The function to do a single try.
 * @param opts Retry options.
 * @returns A promise that will resolve to the returned value from `doTry` if it succeeds, or it's
 * last rejected error otherwise.
 */
export async function retry(doTry, opts) {
    const jitterFactor = opts.jitterFactor ?? 0.1;
    const backoffFactor = opts.backoffFactor ?? 1.1;
    const initialDelayMs = opts.initialDelay?.asMilliseconds() ?? 1_000;
    const fixupDelay = opts.fixupDelay ?? identity;
    const shouldRetry = opts.shouldRetry;
    const shouldRetryOk = opts.shouldRetryOk ?? always(false);
    for (let nthTry = 1;; nthTry++) {
        try {
            // eslint-disable-next-line no-await-in-loop
            const result = await doTry();
            if (!shouldRetryOk(nthTry, result)) {
                return result;
            }
        }
        catch (err) {
            if (!shouldRetry(nthTry, err)) {
                throw err;
            }
        }
        const jitter = 1 + jitterFactor * (2 * Math.random() - 1);
        const backoff = backoffFactor ** (nthTry - 1);
        const delay = moment.duration(initialDelayMs * jitter * backoff, 'ms');
        // eslint-disable-next-line no-await-in-loop
        await sleep(moment.duration(fixupDelay(delay, nthTry)));
    }
}
